import { useState } from 'react';
import { useIntl } from 'react-intl';
import useUser from '@alltrails/context/hooks/useUser';
import IconButtonWithTooltip from '@alltrails/denali/components/IconButtonWithTooltip';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import MapsThreeD from '@alltrails/denali/icons/MapsThreeD';
import MapsTwoD from '@alltrails/denali/icons/MapsTwoD';
import SignUpModal from '@alltrails/user-auth/components/SignUpModal';
import { useAppDispatch, useAppSelector } from '../../redux';
import { updateIs3dActive } from '../../redux/reducer';
import type MapControlButtonOptions from '../../types/MapControlButtonOptions';
import { styleSupports3d } from '../../utils/styleIdHelpers';
import TerrainInstructionsToast from '../TerrainInstructionsToast';
import ThreeDMapsUpsellModal from '../ThreeDMapsUpsellModal/ThreeDMapsUpsellModal';

type TerrainButtonProps = MapControlButtonOptions & {
  onClick?: () => void;
  showInstructionsToast?: boolean;
};

const TerrainButton = ({ elevated = true, onClick, showInstructionsToast = true, size }: TerrainButtonProps) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const user = useUser();
  const isMobile = useIsMobileSizedScreen();
  const [isSignUpModalOpen, setIsSignUpModalOpen] = useState(false);
  const [isThreeDMapsUpsellModalOpen, setIsThreeDMapsUpsellModalOpen] = useState(false);

  const { baseStyleId, is3dActive } = useAppSelector(state => ({
    baseStyleId: state.map.baseStyleId,
    is3dActive: state.map.is3dActive
  }));

  return styleSupports3d(baseStyleId) ? (
    <>
      <IconButtonWithTooltip
        elevated={elevated}
        icon={is3dActive ? { Component: MapsTwoD } : { Component: MapsThreeD }}
        onClick={() => {
          onClick?.();
          if (user?.featureEntitlements?.includes('three_d_map')) {
            dispatch(updateIs3dActive(is3dActive ? false : true));
          } else {
            if (!user) {
              setIsSignUpModalOpen(true);
            } else {
              setIsThreeDMapsUpsellModalOpen(true);
            }
          }
        }}
        size={size}
        title={is3dActive ? intl.formatMessage({ defaultMessage: 'Disable 3D map' }) : intl.formatMessage({ defaultMessage: 'Enable 3D map' })}
        uniqueId="map-terrain-button"
        variant="ghost"
      />
      {showInstructionsToast && !isMobile && <TerrainInstructionsToast />}
      {isSignUpModalOpen && (
        <SignUpModal
          closeModal={() => setIsSignUpModalOpen(false)}
          isOpen={isSignUpModalOpen}
          onSuccess={user => {
            if (!user?.featureEntitlements?.includes('three_d_map')) {
              setIsThreeDMapsUpsellModalOpen(true);
            }
            setIsSignUpModalOpen(false);
          }}
          trigger="three d maps tapped"
          upsellTrigger="three d maps tapped"
        />
      )}
      {isThreeDMapsUpsellModalOpen && (
        <ThreeDMapsUpsellModal isOpen={isThreeDMapsUpsellModalOpen} onCloseRequest={() => setIsThreeDMapsUpsellModalOpen(false)} />
      )}
    </>
  ) : null;
};

export default TerrainButton;
